import { useApolloClient } from '@apollo/client'
import { useState } from 'react'

// Base audience questions are shared across markets (same questionId), but
// their text is per-market (translated). Apollo normalises Question by
// questionId, so these query results must be evicted when switching markets —
// otherwise the previously viewed market's question text sticks (cache-first
// won't refetch). Response options are keyed per survey, so they don't collide.
const AUDIENCE_TEXT_CACHE_FIELDS = [
  'draftAudienceV2',
  'customCriteriaV2',
  'listCategoriesV2'
]

/**
 * Evicts the cross-market audience query results once, during the first render
 * — i.e. before any child mounts its query — so the market being shown
 * refetches and rewrites the shared, per-market question text.
 *
 * Only for surfaces that own the whole audience subtree. Eviction is unscoped
 * across args, so it also drops other surveys' entries for these fields: on a
 * page that watches one of them for a different survey it would force a
 * competing refetch. Use `network-only` on the child query there instead.
 *
 * Not the same rule as the neighbouring `evictAudienceCaches` — that one covers
 * global-question propagation and so evicts `fieldworkAudienceV2` rather than
 * `listCategoriesV2`. The overlap is coincidental; do not merge them.
 */
export const useEvictMarketAudienceTextCache = (): void => {
  const { cache } = useApolloClient()

  useState(() => {
    AUDIENCE_TEXT_CACHE_FIELDS.forEach((fieldName) =>
      cache.evict({ id: 'ROOT_QUERY', fieldName })
    )
    cache.gc()
  })
}
